import React, { useState, useMemo, useEffect } from "react";
import {
    Alert,
    AlertTitle,
    Box,
    Divider,
    InputLabel,
    MenuItem,
    FormControl,
    ListSubheader,
    InputAdornment,
    TextField,
    Select,
    Typography,
} from "@mui/material/";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    useAuditProgressDetails,
    useGroupedAudits,
    useGroupAuditValidation,
} from "../../common/hooks";

export default ({ auditId, onOpenList }) => {
    const [searchText, setSearchText] = useState("");
    const [selectedValue, setSelectedValue] = useState(auditId); // Default value will be set in useEffect

    const { audits, auditLocations } = useSelector((state) => state);

    let navigate = useNavigate();
    const { t } = useTranslation();
    const groupedAudits = useGroupedAudits();
    const { assigned } = useAuditProgressDetails("db", auditId);
    const disableLocation = useGroupAuditValidation(
        assigned,
        auditId,
        "dropdown"
    );

    // Flatten the array to extract relevant options
    const flattenedGroupAudits = useMemo(() => {
        return groupedAudits.map((item) => ({
            auditId: item.audit.id,
            locationName: item.location.name,
            brandOperationCode: item.audit.brandOperation
                ? item.audit.brandOperation.brandOperationCode
                : "",
            // locationName: item.location.name,
        }));
    }, [groupedAudits]);

    // Set the default value as the first option
    useEffect(() => {
        if (flattenedGroupAudits.length > 0) {
            setSelectedValue(auditId);
        }
    }, [flattenedGroupAudits]);

    // Filter the options based on the search text
    const filteredGroupAudits = useMemo(() => {
        if (!searchText) return flattenedGroupAudits;
        return flattenedGroupAudits.filter(
            (item) =>
                item.locationName
                    .toLowerCase()
                    .includes(searchText.toLowerCase())
            //|| item.description
            //     .toLowerCase()
            //     .includes(searchText.toLowerCase()) ||
        );
    }, [flattenedGroupAudits, searchText]);

    const handleChange = (event) => {
        const auditId = event.target.value;
        const audit = audits.data.find((item) => item.id === auditId);

        const url =
            audit.auditReference.toLowerCase() === "none"
                ? `/auth/audits/${auditId}/details`
                : `/auth/audits/${auditId}/groupedAudits`;

        navigate(url);
        location.reload();
        setCurrentAudit(auditId);
        setSelectedValue(event.target.value); // Update selected value
    };

    return (
        <FormControl
            sx={{ m: 1, minWidth: 300 }}
            size="small"
            fullWidth
            onClick={onOpenList}
            // disabled={disableLocation}
        >
            <InputLabel>{t("location")}</InputLabel>
            <Select
                value={selectedValue}
                label={t("location")}
                onChange={onOpenList}
                onClose={() => setSearchText("")}
                MenuProps={{ autoFocus: false }}
                inputProps={{ readOnly: true }}
            >
                {/* Search bar */}
                <ListSubheader style={{ background: "white" }}>
                    <TextField
                        variant="standard"
                        size="small"
                        autoFocus
                        placeholder="Type to search..."
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key !== "Escape") {
                                e.stopPropagation();
                            }
                        }}
                    />
                </ListSubheader>
                {/* Render filtered options */}
                {filteredGroupAudits.map((item) => (
                    <MenuItem
                        key={`${item.auditId} - ${item.name}`}
                        value={item.auditId}
                    >
                        <Box display="flex" flexDirection="column">
                            <Typography noWrap variant="body2">
                                {item.name}
                            </Typography>
                            <Typography
                                noWrap
                                variant="caption"
                                color="text.secondary"
                            >
                                {item.locationName}
                            </Typography>
                            <Typography noWrap variant="body2">
                                {item.brandOperationCode}
                            </Typography>
                            <Divider component="div" />
                        </Box>
                    </MenuItem>
                ))}
            </Select>
            {disableLocation && (
                <Alert variant="outlined" severity="warning">
                    <AlertTitle>{t("warning")}</AlertTitle>
                    {t("messages.validateSignatureMessage")}
                </Alert>
            )}
        </FormControl>
    );
};

/** TODO
 *
 * 1. Modify/Delete this if the modal works on previous screen
 * 2. Leave it for first release
 */
